import { isDefined } from "@omni/utils/helper.utils";
import { createContext, ReactNode, use } from "react";

import {
    CONTENT_PAGES_SOURCE_ID,
    PRODUCTS_SOURCE_ID,
    QUERY_SUGGESTIONS_SOURCE_ID,
    RECENT_SEARCHES_SOURCE_ID
} from "@/components/Search/SearchBar/config/searchBar.config";
import {
    ContentPagesSourceType,
    ProductsSourceType,
    QuerySuggestionsSourceType,
    RecentSearchesSourceType
} from "@/components/Search/SearchBar/types/searchBar.types";

export type Results = {
    [RECENT_SEARCHES_SOURCE_ID]: RecentSearchesSourceType;
    [QUERY_SUGGESTIONS_SOURCE_ID]: QuerySuggestionsSourceType;
    [CONTENT_PAGES_SOURCE_ID]: ContentPagesSourceType;
    [PRODUCTS_SOURCE_ID]: ProductsSourceType;
};

export type Items = {
    [K in keyof Results]: Results[K][];
};

type ResultsContextValue = {
    items: Items;
};

type ResultsContextProviderProps = {
    items: Items;
    children: ReactNode;
};

export const ResultsContext = createContext<ResultsContextValue | undefined>(
    undefined
);

export const ResultsContextProvider = ({
    items,
    children
}: ResultsContextProviderProps) => {
    return (
        <ResultsContext
            value={{
                items: items
            }}
        >
            {children}
        </ResultsContext>
    );
};

export const useResultsContext = () => {
    const context = use<ResultsContextValue | undefined>(ResultsContext);

    if (!isDefined(context)) {
        throw new Error(`${ResultsContext.displayName} is undefined.`);
    }

    return context;
};

export const useResults = <T extends keyof Items>(sourceId: T) => {
    const context = useResultsContext();

    return {
        items: context.items[sourceId] ?? []
    };
};
